// Global Instructions Rule Applied!
import { buildFullUrl, getBackendBaseUrl } from '../utils/endpoints.ts'
import { API_ROUTES } from '../utils/endpoints.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface UploadFileResponse {
  // Row-backed uploads: `id` is the file_urls_* row UUID (always present
  // post-2026-05-28). `type` is the backend's row column value:
  //   'storage' = Supabase Storage object (file_url is a Storage path)
  //   'url'     = external URL the user pasted
  // The internal FileUploadManager constants are 'file'|'url' (legacy);
  // fileTransformers.ts translates 'storage' → 'file' at the boundary.
  id?: string
  file_path: string
  file_name: string
  unique_file_name?: string
  public_url: string
  bucket?: string | null
  uploaded_at: string
  type?: 'storage' | 'url' | 'file' // 'file' kept for any legacy server that hasn't been redeployed
  file_size_bytes?: number | null
}

export interface DeleteFileResponse {
  // Post-2026-05-28 delete response carries the row id + move_to (the
  // _trash/ path) so the UI can show where the file went if needed.
  id?: string
  file_path?: string
  type?: 'storage' | 'url' | 'file'
  deleted_at: string
  moved_to?: string | null
}

export interface FileListItem {
  // List endpoint reads from file_urls_* rows. `id` is always the row
  // UUID (post-2026-05-28). type uses the row column directly.
  id: string
  file_path: string         // Storage path (type='storage') or external URL (type='url')
  file_name: string         // Original filename with spaces preserved for storage uploads
  type?: 'storage' | 'url' | 'file' // 'file' tolerated for legacy responses
  file_url?: string         // Same as file_path; preserved for legacy callers
  file_size_bytes?: number | null
  created_at: string
  updated_at: string
  created_by?: string | null
  modified_by?: string | null
  metadata?: Record<string, any>
}

export interface SignedUrlResponse {
  file_path: string
  signed_url: string
  expires_in: number
}

export const uploadFile = async (
  file: File | null,
  accessToken: string | null,
  contextId?: string | null,
  folderPath?: string | null,
  uploadType: 'file' | 'url' = 'file',
  url?: string,
  fileName?: string,
  tableName?: string | null,
  contextIdColumn?: string | null
): Promise<UploadFileResponse> => {
  try {
    // Create FormData for multipart/form-data upload
    const formData = new FormData()

    if (uploadType === 'url') {
      // URL upload
      formData.append('type', 'url')
      if (url) {
        formData.append('url', url)
      }
      if (fileName) {
        formData.append('fileName', fileName)
      }
      if (contextId) {
        formData.append('context_id', contextId)
      }
      if (tableName) {
        formData.append('table_name', tableName)
      }
      if (contextIdColumn) {
        formData.append('context_id_column', contextIdColumn)
      }
    } else {
      // File upload
      if (file) {
        formData.append('file', file)
      }
      if (contextId) {
        formData.append('context_id', contextId)
      }
      if (folderPath) {
        formData.append('folder_path', folderPath)
      }
      if (tableName) {
        formData.append('table_name', tableName)
      }
      if (contextIdColumn) {
        formData.append('context_id_column', contextIdColumn)
      }
    }

    const headers: Record<string, string> = {}

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }
    // Don't set Content-Type - browser will set it with boundary for multipart/form-data

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FILES.UPLOAD), {
      method: API_ROUTES.FILES.UPLOAD.method,
      headers,
      body: formData
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.error || errorData.message || 'Failed to upload file')
    }

    const data = await response.json()

    if (data.data) {
      return data.data as UploadFileResponse
    } else {
      throw new Error(data.error || data.message || 'Failed to upload file')
    }
  } catch (error) {
    console.error('Error uploading file:', error)
    throw error
  }
}

export const deleteFile = async (
  filePath: string | null,
  accessToken: string | null,
  fileUrlId?: string | null,
  tableName?: string | null
): Promise<DeleteFileResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    // Build request body - include file_path or file_url_id
    const body: Record<string, string> = {}
    if (filePath) {
      body.file_path = filePath
    }
    if (fileUrlId) {
      body.file_url_id = fileUrlId
    }
    if (tableName) {
      body.table_name = tableName
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FILES.DELETE), {
      method: API_ROUTES.FILES.DELETE.method,
      headers,
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.error || errorData.message || 'Failed to delete file')
    }

    const data = await response.json()

    if (data.data) {
      return data.data as DeleteFileResponse
    } else {
      throw new Error(data.error || data.message || 'Failed to delete file')
    }
  } catch (error) {
    console.error('Error deleting file:', error)
    throw error
  }
}

export const getSignedUrl = async (filePath: string, accessToken: string | null): Promise<SignedUrlResponse> => {
  try {
    const headers: Record<string, string> = {}

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const queryParams: Record<string, string> = {
      file_path: filePath
    }

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FILES.GET_SIGNED_URL, {}, queryParams)

    const response = await fetch(url, {
      method: API_ROUTES.FILES.GET_SIGNED_URL.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.error || errorData.message || 'Failed to get signed URL')
    }

    const data = await response.json()

    if (data.data && data.data.signed_url) {
      return data.data as SignedUrlResponse
    } else {
      throw new Error(data.error || data.message || 'Failed to get signed URL')
    }
  } catch (error) {
    console.error('Error getting signed URL:', error)
    throw error
  }
}

export const listFiles = async (
  contextId?: string | null,
  accessToken?: string | null,
  folderPath?: string | null,
  tableName?: string | null,
  contextIdColumn?: string | null
): Promise<FileListItem[]> => {
  try {
    const headers: Record<string, string> = {}

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const queryParams: Record<string, string> = {}
    if (contextId) {
      queryParams.context_id = contextId
    }
    if (folderPath) {
      queryParams.folder_path = folderPath
    }
    if (tableName) {
      queryParams.table_name = tableName
    }
    if (contextIdColumn) {
      queryParams.context_id_column = contextIdColumn
    }

    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FILES.LIST, {}, queryParams)

    const response = await fetch(url, {
      method: API_ROUTES.FILES.LIST.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.error || errorData.message || 'Failed to list files')
    }

    const data = await response.json()

    if (data.data && Array.isArray(data.data)) {
      return data.data as FileListItem[]
    } else {
      return []
    }
  } catch (error) {
    console.error('Error listing files:', error)
    throw error
  }
}
